import React, { useState } from 'react';
import styles from './Auth.module.css';
import { useDispatch } from 'react-redux';
import { updateUserProfile } from '../features/userSlice';
import { auth, provider, storage } from '../firebase';

import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Paper,
  Grid,
  Typography,
  makeStyles,
  Modal,
  IconButton,
  Box,
  Collapse,
} from '@material-ui/core';

// import Alert from '@material-ui/lab/Alert';
import Alert from '@mui/material/Alert';
import SendIcon from '@material-ui/icons/Send';
import CameraIcon from '@material-ui/icons/Camera';
import EmailIcon from '@material-ui/icons/Email';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
// import { AlternateEmailRounded } from '@material-ui/icons';

import { envName } from '../xross.config.js';

// モーダルカードの表示関数
function getModalStyle() {
  const top = 50;
  const left = 50;
  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
  },
  image: {
    backgroundImage: 'url(https://source.unsplash.com/random)',
    backgroundRepeat: 'no-repeat',
    backgroundColor:
      theme.palette.type === 'light'
        ? theme.palette.grey[50]
        : theme.palette.grey[900],
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  paper: {
    margin: theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  modal: {
    outline: 'none',
    position: 'absolute',
    width: 400, // ← 修正: with -> width
    borderRadius: 10,
    backgroundColor: 'white',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(10),
  },
}));

// 強めのプライバシー設定（user-not-found / wrong-password を同文言に）
const STRICT_PRIVACY = true;

function mapAuthError(e: any): {
  form?: string;
  email?: string;
  password?: string;
} {
  const code: string = e?.code || '';

  // デフォルト（包括的な安全文言）
  let result: { form?: string; email?: string; password?: string } = {
    form: 'ログインに失敗しました。メールアドレスまたはパスワードをご確認ください。',
  };

  switch (code) {
    case 'auth/invalid-email':
      result = { email: 'メールアドレスの形式が正しくありません。' };
      break;
    case 'auth/missing-password':
      result = { password: 'パスワードを入力してください。' };
      break;
    case 'auth/weak-password': // signUp 時
      result = {
        password: 'パスワードが弱すぎます（6文字以上にしてください）。',
      };
      break;
    case 'auth/too-many-requests':
      result = {
        form: '試行回数が多すぎます。しばらく待ってから再度お試しください。',
      };
      break;
    case 'auth/network-request-failed':
      result = {
        form: 'ネットワークエラーが発生しました。通信環境をご確認ください。',
      };
      break;
    case 'auth/user-disabled':
      result = {
        form: 'このアカウントは利用できません。管理者にお問い合わせください。',
      };
      break;
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      if (!STRICT_PRIVACY) {
        result =
          code === 'auth/wrong-password'
            ? { form: 'パスワードが違います。' }
            : { form: 'ユーザーが見つかりません。' };
      } else {
        result = { form: 'メールアドレスまたはパスワードが正しくありません。' };
      }
      break;
    default:
      result = { form: '処理に失敗しました。時間をおいて再度お試しください。' };
  }
  return result;
}

const Auth: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // フォーム state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [avatarImage, setAvatarImage] = useState<File | null>(null);

  const [isLogin, setIsLogin] = useState(true); // 初期値はログインモード
  const [resetEmail, setResetEmail] = useState('');

  const [openModal, setOpenModal] = useState(false); // パスワードリセットモーダル

  // エラー & ローディング state
  const [formError, setFormError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // パスワードリセットメール送信
  const sendResetEmail = async () => {
    try {
      await auth.sendPasswordResetEmail(resetEmail);
      setOpenModal(false);
      setResetEmail('');
    } catch (err: any) {
      const mapped = mapAuthError(err);
      setFormError(mapped.form || 'パスワードリセットに失敗しました。');
      setResetEmail('');
    }
  };

  const onChangeImageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAvatarImage(e.target.files[0]);
      e.target.value = ''; // 同じファイル選択でも onChange が走るよう初期化
    }
  };

  /*
   * -----------------------------------------------------------------
   * email でログイン
   * -----------------------------------------------------------------
   */
  const signInEmail = async () => {
    setLoading(true);
    setFormError(null);
    setEmailError(null);
    setPasswordError(null);
    try {
      await auth.signInWithEmailAndPassword(email, password);
    } catch (err: any) {
      const mapped = mapAuthError(err);
      setFormError(mapped.form || null);
      setEmailError(mapped.email || null);
      setPasswordError(mapped.password || null);
    } finally {
      setLoading(false);
    }
  };

  /*
   * -----------------------------------------------------------------
   * email でサインアップ
   * -----------------------------------------------------------------
   */
  const signUpEmail = async () => {
    setLoading(true);
    setFormError(null);
    setEmailError(null);
    setPasswordError(null);

    try {
      const authUser = await auth.createUserWithEmailAndPassword(
        email,
        password
      );

      // firebase に格納する avatar のURL
      let url = '';
      if (avatarImage) {
        const S =
          'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const N = 16;
        const randomChar = Array.from(
          crypto.getRandomValues(new Uint32Array(N))
        )
          .map((n) => S[n % S.length])
          .join('');
        const fileName = randomChar + '_' + avatarImage.name;

        await storage.ref(`avatars/${fileName}`).put(avatarImage);
        url = await storage.ref('avatars').child(fileName).getDownloadURL();
      }

      await authUser.user?.updateProfile({
        displayName: username,
        photoURL: url,
      });

      dispatch(
        updateUserProfile({
          displayName: username,
          photoUrl: url,
          xrossUserID: '',
        })
      );
    } catch (err: any) {
      const mapped = mapAuthError(err);
      setFormError(mapped.form || null);
      setEmailError(mapped.email || null);
      setPasswordError(mapped.password || null);
    } finally {
      setLoading(false);
    }
  };

  // Googleサインイン（必要なときだけ有効化）
  // const signInGoogle = async () => {
  //   try {
  //     setLoading(true);
  //     setFormError(null);
  //     await auth.signInWithPopup(provider);
  //   } catch (err: any) {
  //     const mapped = mapAuthError(err);
  //     setFormError(mapped.form || 'Googleサインインに失敗しました。');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <Grid container component="main" className={classes.root}>
      <CssBaseline />
      <Grid item xs={false} sm={4} md={7} className={classes.image} />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <div className={classes.paper}>
          <Typography component="h1" variant="h5">
            {envName}
          </Typography>
          <Typography component="h1" variant="h5">
            xross.club 運営管理システム（Moon）
          </Typography>

          <Avatar className={classes.avatar}>
            <LockOutlinedIcon />
          </Avatar>

          <Typography component="h1" variant="h5">
            {isLogin ? 'Login' : 'Register'}
          </Typography>

          {/* フォーム全体のエラー（安全表現） */}
          <Collapse in={!!formError} style={{ width: '100%' }}>
            <Alert severity="error" onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          </Collapse>

          <form
            className={classes.form}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (isLogin) {
                void signInEmail();
              } else {
                void signUpEmail();
              }
            }}
          >
            {/* Register のときだけ表示 */}
            {!isLogin && (
              <>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUsername(e.target.value);
                  }}
                />
                <Box textAlign="center">
                  <IconButton>
                    <label>
                      <AccountCircleIcon
                        fontSize="large"
                        className={
                          avatarImage
                            ? styles.login_addIconLoaded
                            : styles.login_addIcon
                        }
                      />
                      <input
                        className={styles.login_hiddenIcon}
                        type="file"
                        onChange={onChangeImageHandler}
                      />
                    </label>
                  </IconButton>
                </Box>
              </>
            )}

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              error={!!emailError}
              helperText={emailError || ' '}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
            />

            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              error={!!passwordError}
              helperText={passwordError || ' '}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
              }}
            />

            <Button
              type="submit"
              disabled={
                loading ||
                (isLogin
                  ? !email || password.length < 6
                  : !username || !email || password.length < 6 || !avatarImage)
              }
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              startIcon={<EmailIcon />}
            >
              {loading ? '処理中…' : isLogin ? 'Login' : 'Register'}
            </Button>

            {/* ↓ 必要に応じて復活させてOK（本番はサインアップUIを隠す等の運用も） */}
            {/* <Grid container>
              <Grid item xs>
                <span
                  className={styles.login_reset}
                  onClick={() => setOpenModal(true)}
                >
                  Forgot password ?
                </span>
              </Grid>
              <Grid item>
                <span
                  className={styles.login_toggleMode}
                  onClick={() => setIsLogin(!isLogin)}
                >
                  {isLogin ? 'Create new account ?' : 'Back to Login'}
                </span>
              </Grid>
            </Grid> */}

            {/* <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<CameraIcon />}
              className={classes.submit}
              onClick={signInGoogle}
              disabled={loading}
            >
              Sign In with Google
            </Button> */}
          </form>

          {/* パスワードリセットモーダル */}
          <Modal open={openModal} onClose={() => setOpenModal(false)}>
            <div style={getModalStyle()} className={classes.modal}>
              <div className={styles.login_modal}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  type="email"
                  name="email"
                  label="Reset E-mail"
                  value={resetEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setResetEmail(e.target.value);
                  }}
                />
                <IconButton onClick={sendResetEmail}>
                  <SendIcon />
                </IconButton>
              </div>
            </div>
          </Modal>
        </div>
      </Grid>
    </Grid>
  );
};

export default Auth;

// import React, { useState } from 'react';
// import styles from './Auth.module.css';
// import { useDispatch } from 'react-redux';
// import { updateUserProfile } from '../features/userSlice';
// import { auth, provider, storage } from '../firebase';

// import {
//   Avatar,
//   Button,
//   CssBaseline,
//   TextField,
//   Paper,
//   Grid,
//   Typography,
//   makeStyles,
//   Modal,
//   IconButton,
//   Box,
// } from '@material-ui/core';

// import SendIcon from '@material-ui/icons/Send';
// import CameraIcon from '@material-ui/icons/Camera';
// import EmailIcon from '@material-ui/icons/Email';
// import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
// import AccountCircleIcon from '@material-ui/icons/AccountCircle';
// import { AlternateEmailRounded } from '@material-ui/icons';

// import { envName } from '../xross.config.js';

// // モーダルカードの表示関数
// function getModalStyle() {
//   const top = 50;
//   const left = 50;

//   return {
//     top: `${top}%`,
//     left: `${left}%`,
//     transform: `translate(-${top}%, -${left}%)`,
//   };
// }

// const useStyles = makeStyles((theme) => ({
//   root: {
//     height: '100vh',
//   },
//   image: {
//     backgroundImage: 'url(https://source.unsplash.com/random)',
//     backgroundRepeat: 'no-repeat',
//     backgroundColor:
//       theme.palette.type === 'light'
//         ? theme.palette.grey[50]
//         : theme.palette.grey[900],
//     backgroundSize: 'cover',
//     backgroundPosition: 'center',
//   },
//   paper: {
//     margin: theme.spacing(8, 4),
//     display: 'flex',
//     flexDirection: 'column',
//     alignItems: 'center',
//   },
//   avatar: {
//     margin: theme.spacing(1),
//     backgroundColor: theme.palette.secondary.main,
//   },
//   form: {
//     width: '100%', // Fix IE 11 issue.
//     marginTop: theme.spacing(1),
//   },
//   submit: {
//     margin: theme.spacing(3, 0, 2),
//   },
//   modal: {
//     outline: 'none',
//     position: 'absolute',
//     with: 400,
//     borderRadius: 10,
//     backgroundColor: 'white',
//     boxShadow: theme.shadows[5],
//     padding: theme.spacing(10),
//   },
// }));

// const Auth: React.FC = () => {
//   const classes = useStyles();
//   const dispatch = useDispatch();
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const [username, setUsername] = useState('');
//   const [avatarImage, setAvatarImage] = useState<File | null>(null);

//   const [isLogin, setIsLogin] = useState(true); //初期値はログインモード
//   const [resetEmail, setResetEmail] = useState('');

//   const [openModal, setOpenModal] = useState(false); //初期値はモーダルが閉じている状態

//   // パスワードリセットのメールを送る関数
//   const sendResetEmail = async (e: React.MouseEvent<HTMLElement>) => {
//     await auth
//       .sendPasswordResetEmail(resetEmail)
//       .then(() => {
//         setOpenModal(false);
//         setResetEmail('');
//       })
//       .catch((err) => {
//         alert(err.message);
//         setResetEmail('');
//       });
//   };

//   const onChangeImageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
//     if (e.target.files![0]) {
//       // "!"" - non-null assertion operator - コンパイラにこの値はnull,undefinedではなないと伝えるもの。
//       //配列の0番目だけを判定する
//       //存在すればavatarImage にセットする
//       setAvatarImage(e.target.files![0]);
//       e.target.value = ''; //初期化することによって、onChangeImageHandler が毎回反応する
//     }
//   };

//   /*
//    * -----------------------------------------------------------------
//    * email でログイン
//    * -----------------------------------------------------------------
//    */
//   const signInEmail = async () => {
//     await auth.signInWithEmailAndPassword(email, password);
//   };

//   /*
//    * -----------------------------------------------------------------
//    * email でサインアップ
//    * -----------------------------------------------------------------
//    */
//   const signUpEmail = async () => {
//     const authUser = await auth.createUserWithEmailAndPassword(email, password);
//     // firebase に格納するavator のURL
//     let url = '';
//     // avatarImage が存在していれば格納する
//     if (avatarImage) {
//       // fileName はユニークである必要があるためランダムに生成する
//       const S =
//         'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
//       // ランダムな16文字を生成する
//       const N = 16;
//       const randomChar = Array.from(crypto.getRandomValues(new Uint32Array(N)))
//         .map((n) => S[n % S.length])
//         .join('');
//       const fileName = randomChar + '_' + avatarImage.name;

//       // storage の avatar フォルダに格納する
//       await storage.ref(`avatars/${fileName}`).put(avatarImage);
//       url = await storage.ref('avatars').child(fileName).getDownloadURL();
//     }
//     await authUser.user?.updateProfile({
//       displayName: username,
//       photoURL: url,
//     });
//     dispatch(
//       updateUserProfile({
//         displayName: username,
//         photoUrl: url,
//         xrossUserID: '',
//       })
//     );
//   };

//   // Googleサインイン
//   // const signInGoogle = async () => {
//   //   // 非同期関数
//   //   // 処理が終わるまで待つ
//   //   await auth.signInWithPopup(provider).catch((err) => alert(err.message));
//   // };

//   return (
//     <Grid container component="main" className={classes.root}>
//       <CssBaseline />
//       <Grid item xs={false} sm={4} md={7} className={classes.image} />
//       <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
//         <div className={classes.paper}>
//           <Typography component="h1" variant="h5">
//             {envName}
//           </Typography>
//           <Typography component="h1" variant="h5">
//             xross.club 運営管理システム（Moon）
//           </Typography>

//           <Avatar className={classes.avatar}>
//             <LockOutlinedIcon />
//           </Avatar>

//           <Typography component="h1" variant="h5">
//             {isLogin ? 'Login' : 'Register'}
//           </Typography>

//           <form className={classes.form} noValidate>
//             {!isLogin && (
//               <>
//                 <TextField
//                   variant="outlined"
//                   margin="normal"
//                   required
//                   fullWidth
//                   id="username"
//                   label="Username"
//                   name="username"
//                   autoComplete="username"
//                   autoFocus
//                   value={username}
//                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
//                     setUsername(e.target.value);
//                   }}
//                 />
//                 <Box textAlign="center">
//                   <IconButton>
//                     <label>
//                       <AccountCircleIcon
//                         fontSize="large"
//                         className={
//                           avatarImage
//                             ? styles.login_addIconLoaded
//                             : styles.login_addIcon
//                         }
//                       />
//                       <input
//                         className={styles.login_hiddenIcon}
//                         type="file"
//                         onChange={onChangeImageHandler}
//                       />
//                     </label>
//                   </IconButton>
//                 </Box>
//               </>
//             )}
//             <TextField
//               variant="outlined"
//               margin="normal"
//               required
//               fullWidth
//               id="email"
//               label="Email Address"
//               name="email"
//               autoComplete="email"
//               autoFocus
//               value={email}
//               onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
//                 setEmail(e.target.value);
//               }}
//             />
//             <TextField
//               variant="outlined"
//               margin="normal"
//               required
//               fullWidth
//               name="password"
//               label="Password"
//               type="password"
//               id="password"
//               autoComplete="current-password"
//               value={password}
//               onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
//                 setPassword(e.target.value);
//               }}
//             />
//             <Button
//               disabled={
//                 isLogin
//                   ? !email || password.length < 6
//                   : !username || !email || password.length < 6 || !avatarImage
//               }
//               fullWidth
//               variant="contained"
//               color="primary"
//               className={classes.submit}
//               startIcon={<EmailIcon />}
//               onClick={
//                 isLogin
//                   ? async () => {
//                       try {
//                         await signInEmail();
//                       } catch (err: any) {
//                         alert(err.message);
//                       }
//                     }
//                   : async () => {
//                       try {
//                         await signUpEmail();
//                       } catch (err: any) {
//                         alert(err.message);
//                       }
//                     }
//               }
//             >
//               {isLogin ? 'Login' : 'Register'}
//             </Button>
//             {/* <Grid container>
//               <Grid item xs>
//                 <span
//                   className={styles.login_reset}
//                   onClick={() => setOpenModal(true)}
//                 >
//                   Forgot password ?
//                 </span>
//               </Grid>
//               <Grid item>
//                 <span
//                   className={styles.login_toggleMode}
//                   onClick={() => setIsLogin(!isLogin)}
//                 >
//                   {isLogin ? 'Create new account ?' : 'Back to Login'}
//                 </span>
//               </Grid>
//             </Grid> */}
//             {/* <Button
//               fullWidth
//               variant="contained"
//               color="primary"
//               startIcon={<CameraIcon />}
//               className={classes.submit}
//               onClick={signInGoogle}
//             >
//               Sign In　with Google
//             </Button> */}
//           </form>

//           <Modal open={openModal} onClose={() => setOpenModal(false)}>
//             <div style={getModalStyle()} className={classes.modal}>
//               <div className={styles.login_modal}>
//                 <TextField
//                   InputLabelProps={{
//                     shrink: true,
//                   }}
//                   type="email"
//                   name="email"
//                   label="Reset E-mail"
//                   value={resetEmail}
//                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
//                     setResetEmail(e.target.value);
//                   }}
//                 />
//                 <IconButton onClick={sendResetEmail}>
//                   <SendIcon />
//                 </IconButton>
//               </div>
//             </div>
//           </Modal>
//         </div>
//       </Grid>
//     </Grid>
//   );
// };
// export default Auth;
